import {
  QueryClient,
  type QueryFunction,
  type QueryKey,
  type QueryOptions
} from "@tanstack/react-query";
import { ERRORS } from "../consts";
import { useAuthStore } from "../stores";
import { waitForSessionTransition } from "../stores/authStore";
import type { LoadersList } from "../types";
import { getError } from "../utils/error";

/**
 * react query's query objects
 */
type QueryObject = {
  queryFn: QueryFunction
  queryKey: QueryKey
  options?: QueryOptions
}

// let refreshPromise: Promise<void> | null = null;

function handleResponse(
  res: Response,
  resolve: (value: Response) => void,
  reject: (reason: unknown) => void
) {
  if (!res.ok) {
    // case error 400 with custom codes for selfsignup
    if (res.status === 400) {
      return res.json().then((response) => {
        const errorObj = response?.errors?.FATAL && response?.errors?.FATAL[0];
        if ((errorObj?.code === ERRORS.REGISTRATION_NOT_VALID) || (errorObj?.code === ERRORS.REGISTRATION_NOT_ALLOWED) || (errorObj?.code === ERRORS.ALREADY_REGISTERED)){
          reject(errorObj?.message);
        }else{
          reject(JSON.stringify(response));
        }
      });
    } else{
      res
        .text()
        .then((response) => { reject(response) }); // what if the response has no text?
    }
  } else {
    resolve(res);
  }
}

export function fetcherFactory() {
  // NOTE: to mock an expired session use:
  // eslint-disable-next-line max-len
  // https://web-learning-session-manager-v1.openlearning-svil.digitedacademy.net/swagger-ui/index.html#/session-test-controller/removeSessionUsingDELETE
  return async function (
    url: RequestInfo | URL,
    option: RequestInit,
    skipSessionTransition?: boolean
  ): Promise<Response> {
   
    let effectiveOption = option;
    let preState = useAuthStore.getState();

    const sessionTransition = waitForSessionTransition();
    if (sessionTransition && !skipSessionTransition) {
      const tokenOriginale = new Headers(option.headers).get("x-ada-session-token");
      // eslint-disable-next-line no-console
      console.log("fetch IN CODA su session transition", { tokenOriginale, url: String(url) });
      await sessionTransition;
      const state = useAuthStore.getState();
      const headers = new Headers(option.headers);

      if (state.session && state.sessionToken) {
        headers.set("authorization", state.session.getAccessToken().getJwtToken());
        headers.set("x-ada-session-token", state.sessionToken);
        effectiveOption = { ...option, headers };
        // eslint-disable-next-line no-console
        console.log("fetch HEADER SOVRASCRITTI", {
          tokenNuovo: state.sessionToken,
          tokenOriginale,
          url: String(url)
        });
      }
    }
    preState = useAuthStore.getState();
    // Refresh proattivo: se la sessione è già scaduta prima di inviare la
    // richiesta, rinnoviamo i token e aggiorniamo gli header in modo che la
    // prima fetch parta già autenticata (speculare al request interceptor di admin).
    if (!skipSessionTransition && preState.session && preState.sessionToken && !preState.session.isValid()) {
      try {
        await useAuthStore.getState().refreshSession();
        const freshState = useAuthStore.getState();
        if (freshState.session && freshState.sessionToken) {
          const freshHeaders = new Headers(option.headers);
          freshHeaders.set("authorization", freshState.session.getAccessToken().getJwtToken());
          freshHeaders.set("x-ada-session-token", freshState.sessionToken);
          effectiveOption = { ...option, headers: freshHeaders };
        }
      } catch {
        // Il refresh fallisce: la richiesta parte con i vecchi header,
        // il blocco reattivo su 401 gestirà il caso.
      }
    }
    
    

    return new Promise((resolve, reject) => {
      fetch(url, effectiveOption)
        .then(async (res) => {
          if (res.status === 401) {
            const currentState = useAuthStore.getState();
            // eslint-disable-next-line no-console
            console.log("fetch 401", {
              haSessione: !!currentState.session,
              skipSessionTransition: !!skipSessionTransition,
              url: String(url)
            });
            if (!currentState.session || !currentState.sessionToken) {
              reject(ERRORS.UNAUTHORIZED);
              return;
            }
            if (skipSessionTransition) {
              reject(ERRORS.UNAUTHORIZED);
              return;
            }
            try {
              await useAuthStore.getState().refreshSession();

              const state = useAuthStore.getState();
              if (state.session && state.sessionToken) {

                const retryHeaders = new Headers(option.headers);
                retryHeaders.set("authorization", state.session.getAccessToken().getJwtToken());
                retryHeaders.set("x-ada-session-token", state.sessionToken);

                const retryRes = await fetch(url, { ...option, headers: retryHeaders });
                if (retryRes.status === 401) {
                  reject(ERRORS.UNAUTHORIZED);
                } else {
                  handleResponse(retryRes, resolve, reject);
                }
              } else {
                reject(ERRORS.UNAUTHORIZED);
              }
            } catch {
              reject(ERRORS.UNAUTHORIZED);
            }
          } else {
            handleResponse(res, resolve, reject);
          }
        })
        /**
         * NOTE, in some circumstances fetch was failing with
         * a 401 without resolving the promise of the response
         */
        .catch((error: unknown) => {
          const message = getError(error)?.[0]?.message ?? "Failed to fetch";
          reject(message);
        });
    });
  };
}

type LoaderFactory = (queries: QueryObject[]) => () => Promise<LoadersList>

/** loaderFactory
 * creates a function to be used with react router-6 and react-query
 * cfr. https://reactrouter.com/en/main/guides/deferred for alternate pattern
 * @param queryClient
 * @returns a function to be used in the router declaration
 */
export function loaderFactory(queryClient: QueryClient): LoaderFactory {
  /**
  * @param queries an array of react-query queries
  * @returns a LoaderFunction for react-router 6
  */
  return function (queries: QueryObject[] ) {
  /**
  * @returns a map of stringified query keys to cache contents (if available)
  * or promises which resolve to a query result (if no cache is available)
  */
    return async function () {
      return (queries ?? [])
        .reduce(async(acc, query) => ({
          ...acc,
          [query.queryKey.toString()]:
            queryClient.ensureQueryData(query)
        }), {});
    };
  };
}
